import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../components/common/GradientCircularProgress';
import TableCard, { TableColumn } from '../../../components/common/TableCard';
import useBackendApi from '../../../hooks/useBackendApi';
import { RequestOptions } from '../../../types/httpRequests';
import { AccountType } from '../../../types/account';
import BorderedButton from '../../../components/common/buttons/BorderedButton';
import { useGhostButtonProps } from '../../../hooks/useGhostButtonProps';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

import AccountDrawer, { AccountDrawerMode, AccountDrawerPayload } from './AccountDrawer';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type AccountsResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  accounts?: AccountType[];
  user_org_roles?: string[];
  user_acc_roles?: string[];
};

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

export default function Accounts(): JSX.Element {
  const { org_path_name } = useParams<{ org_path_name: string }>();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const addAccountButtonProps = useGhostButtonProps({
    default: '/icons/store-blue900.svg',
    hover: '/icons/store-blue500.svg',
  });

  const [accounts, setAccounts] = useState<AccountType[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [userAccRoles, setUserAccRoles] = useState<string[]>([]);

  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const isAnyMenuOpen = useMemo(() => Object.values(menuAnchorEl).some(Boolean), [menuAnchorEl]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<AccountDrawerMode>('create');
  const [drawerInitialAccount, setDrawerInitialAccount] = useState<AccountType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOrgAdmin = userOrgRoles.some((r) => r?.toLowerCase() === 'admin');
  const isAccAdmin = userAccRoles.some((r) => r?.toLowerCase() === 'admin');

  const canAddAccount = isOrgAdmin || isAccAdmin;
  const canEditAccount = isOrgAdmin || isAccAdmin;

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const handleCommonAuthNav = useCallback(
    (resStatus?: number, body?: { status_code?: number; message?: string; detail?: string }) => {
      if (
        resStatus === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return true;
      }
      if (resStatus === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return true;
      }
      return false;
    },
    [navigate],
  );

  const fetchAccounts = useCallback(async () => {
    if (!org_path_name) return;

    const opts: RequestOptions = {
      endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/`,
      method: 'GET',
    };

    try {
      const res = await apiFetchRequest(opts);
      if (!res) {
        fail();
        return;
      }

      const body = res.body as AccountsResponse;
      if (handleCommonAuthNav(res.status, body)) return;

      setUserOrgRoles(Array.isArray(body?.user_org_roles) ? body.user_org_roles : []);
      setUserAccRoles(Array.isArray(body?.user_acc_roles) ? body.user_acc_roles : []);

      if (res.isError || res.status !== 200 || body?.message !== 'SUCCESS') {
        if (res.status === 404 || body?.detail === 'Account not found') {
          setAccounts([]);
          setErrorText(null);
          return;
        }
        fail();
        return;
      }

      setAccounts(Array.isArray(body?.accounts) ? body.accounts : []);
      setErrorText(null);
    } catch {
      fail();
    }
  }, [apiFetchRequest, fail, handleCommonAuthNav, org_path_name]);

  useEffect(() => {
    if (!org_path_name) {
      navigate('/404.html');
      return;
    }
    setActionMessage(null);
    void fetchAccounts();
  }, [fetchAccounts, navigate, org_path_name]);

  const onRowClick = useCallback(
    (acc: AccountType) => {
      if (isAnyMenuOpen) return;
      navigate(`/o/${encodeURIComponent(org_path_name!)}/a/${encodeURIComponent(acc.path_name)}`);
    },
    [isAnyMenuOpen, navigate, org_path_name],
  );

  const openCreateDrawer = useCallback(() => {
    if (!canAddAccount) return;
    setActionMessage(null);
    setDrawerMode('create');
    setDrawerInitialAccount(null);
    setIsDrawerOpen(true);
  }, [canAddAccount]);

  const openEditDrawer = useCallback(
    (acc: AccountType) => {
      if (!canEditAccount) return;
      setActionMessage(null);
      setDrawerMode('edit');
      setDrawerInitialAccount(acc);
      setIsDrawerOpen(true);
    },
    [canEditAccount],
  );

  const closeDrawer = useCallback(() => {
    if (isSubmitting) return;
    setIsDrawerOpen(false);
    setDrawerMode('create');
    setDrawerInitialAccount(null);
  }, [isSubmitting]);

  const handleOpenMenu = useCallback((e: MouseEvent<HTMLElement>, key: string) => {
    e.stopPropagation();
    setMenuAnchorEl((prev) => ({ ...prev, [key]: e.currentTarget }));
  }, []);

  const handleCloseMenu = useCallback((key: string) => {
    setMenuAnchorEl((prev) => ({ ...prev, [key]: null }));
  }, []);

  const handleSubmitDrawer = useCallback(
    async (payload: AccountDrawerPayload) => {
      if (!org_path_name) return;

      setIsSubmitting(true);
      setActionMessage(null);

      try {
        if (drawerMode === 'create') {
          if (!canAddAccount) return;

          const res = await apiFetchRequest({
            endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/`,
            method: 'POST',
            body: payload,
          });

          if (!res) {
            setActionMessage({ type: 'error', text: 'Unable to create account.' });
            return;
          }

          const body = res.body as { message?: string; detail?: string; status_code?: number };
          if (handleCommonAuthNav(res.status, body)) return;

          if (res.isError || body?.message !== 'SUCCESS') {
            setActionMessage({ type: 'error', text: body?.detail || 'Unable to create account.' });
            return;
          }

          setActionMessage({ type: 'success', text: 'Account created successfully.' });
          await fetchAccounts();
          closeDrawer();
          return;
        }

        if (!canEditAccount) return;

        const targetPath = drawerInitialAccount?.path_name;
        if (!targetPath) {
          setActionMessage({ type: 'error', text: 'Unable to update account.' });
          return;
        }

        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(targetPath)}`,
          method: 'PUT',
          body: payload,
        });

        if (!res) {
          setActionMessage({ type: 'error', text: 'Unable to update account.' });
          return;
        }

        const body = res.body as { message?: string; detail?: string; status_code?: number };
        if (handleCommonAuthNav(res.status, body)) return;

        if (res.isError || (res.status !== 200 && res.status !== 204) || body?.message !== 'SUCCESS') {
          setActionMessage({ type: 'error', text: body?.detail || 'Unable to update account.' });
          return;
        }

        setActionMessage({ type: 'success', text: 'Account updated successfully.' });
        await fetchAccounts();
        closeDrawer();
      } catch {
        setActionMessage({ type: 'error', text: 'Unable to save account. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      apiFetchRequest,
      canAddAccount,
      canEditAccount,
      closeDrawer,
      drawerInitialAccount,
      drawerMode,
      fetchAccounts,
      handleCommonAuthNav,
      org_path_name,
    ],
  );

  const renderActions = useCallback(
    (acc: AccountType) => {
      if (!canEditAccount) return null;

      const key = String(acc.acc_id ?? acc.path_name);

      return (
        <>
          <IconButton
            aria-label="Account actions"
            onClick={(e) => handleOpenMenu(e, key)}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchorEl[key] ?? null}
            open={Boolean(menuAnchorEl[key])}
            onClose={() => handleCloseMenu(key)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{
              onClick: (e) => e.stopPropagation(),
            }}
          >
            {canEditAccount ? (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseMenu(key);
                  openEditDrawer(acc);
                }}
              >
                Edit Account
              </MenuItem>
            ) : null}
          </Menu>
        </>
      );
    },
    [
      canEditAccount,
      handleCloseMenu,
      handleOpenMenu,
      menuAnchorEl,
      openEditDrawer,
    ],
  );

  const columns = useMemo<TableColumn<AccountType>[]>(() => {
    const base: TableColumn<AccountType>[] = [
      { label: 'Name', render: (acc) => acc.name },
      { label: 'Description', render: (acc) => acc.description || 'N/A' },
    ];

    if (canEditAccount) {
      base.push({ label: 'Actions', render: (acc) => renderActions(acc) });
    }

    return base;
  }, [canEditAccount, renderActions]);

  if ((isLoading && !errorText && accounts.length === 0) || !org_path_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Accounts
          </Typography>
        </Box>

        {canAddAccount ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <BorderedButton
              onClick={openCreateDrawer}
              buttonText="Add Account"
              {...addAccountButtonProps}
            />
          </Box>
        ) : null}
      </Box>

      {actionMessage && (
        <Box sx={{ px: '40px', mb: 4 }}>
          <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)}>
            {actionMessage.text}
          </Alert>
        </Box>
      )}

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        <Box>
          {accounts.length ? (
            <TableCard
              title=""
              rows={accounts}
              columns={columns}
              getRowKey={(acc) => acc.acc_id ?? acc.path_name}
              onRowClick={onRowClick}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ mb: canAddAccount ? 2 : 0 }}>
                No accounts available
              </Typography>
              {canAddAccount && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BorderedButton
                    onClick={openCreateDrawer}
                    buttonText="Add Account"
                    {...addAccountButtonProps}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AccountDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialAccount={drawerInitialAccount}
        onClose={closeDrawer}
        onSubmit={handleSubmitDrawer}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
